import { LinkIcon, PaperAirplaneIcon, ShareIcon } from '@heroicons/react/20/solid';
import BasicModal from '../../../components/modals/BasicModal';
import Image from '../../../components/Image';
import { WithTooltip } from '../../../components/WithTooltip';
import { useEffect, useState } from 'react';
import { constructMailToLinkForUser } from '../../../utils/users';
import { useReferralCode, useShareUrl } from '../../../hooks/useShareUrl';
import { withReferral } from '../../../utils/shareUrl';
import { useUser } from '@clerk/react';
import { LINK_SOURCE } from '../../../constants/linkSources';
import { getTitleImageFromBlocks } from '../../Packet/utils/utils';
import { Mixpanel } from '../../../services/Mixpanel';

const shareProviders = [
  {
    actionText: 'Email',
    provider: 'email',
    icon: <PaperAirplaneIcon className='h-fit w-auto ml-1 text-gray-400' />,
    onClick: (packet, user, shareUrl) => {
      let bodyForEmail = encodeURIComponent(
        `I made this resource about "${packet.title.trim()}" using https://diffit.me --\n\n${shareUrl}\n\nYou can use Diffit to make materials like this - but for anything you're teaching!`
      );
      const subject = `Check out this resource from Diffit: ${packet.title.trim()}`;
      const url = constructMailToLinkForUser({ user, subject, body: bodyForEmail });
      window.open(url, '_blank');
    },
  },
  {
    actionText: 'Twitter',
    provider: 'twitter',
    icon: <Image alt='Twitter' src='/twitter-logo.png' className='h-fit w-auto text-gray-400' />,
    onClick: (packet, user, shareUrl) => {
      const encodedUrl = encodeURIComponent(shareUrl);
      const text = encodeURIComponent(
        'Check out this resource I created for my students using @DiffitApp:\n\n'
      );
      const url = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${text}`;
      window.open(url, '_blank');
    },
  },
  {
    actionText: 'Facebook',
    provider: 'facebook',
    icon: <Image alt='Facebook' src='/facebook-logo.png' className='h-fit w-auto text-gray-400' />,
    onClick: (packet, user, shareUrl) => {
      const encodedUrl = encodeURIComponent(shareUrl);
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
      window.open(url, '_blank');
    },
  },
  {
    actionText: 'Pinterest',
    provider: 'pinterest',
    icon: (
      <Image alt='Pinterest' src='/pinterest-logo.png' className='h-fit w-auto text-gray-400' />
    ),
    onClick: (packet, user, shareUrl) => {
      const packetTitleTruncated =
        packet.title.length > 100 ? packet.title.slice(0, 100) + '...' : packet.title;
      const packetUrlEncoded = encodeURIComponent(shareUrl);
      const body = encodeURIComponent(
        `I made this resource about "${packetTitleTruncated}" using https://diffit.me --\n\n${shareUrl}\n\nYou can use Diffit to make materials like this - but for anything you're teaching!`
      );
      const packetImage = packet.blocks ? getTitleImageFromBlocks(packet.blocks) : null;
      const packetImageUrl = encodeURIComponent(packetImage ?? '');
      const url = `https://pinterest.com/pin/create/link/?description=${body}&url=${packetUrlEncoded}&media=${packetImageUrl}`;
      window.open(url, '_blank');
    },
  },
];

export const SharePacketModal = ({ packet, isProject = false, onClose }) => {
  const packetTitleTruncated =
    packet.title.length > 100 ? packet.title.slice(0, 100) + '...' : packet.title;
  const packetUrl = isProject
    ? `${window.location.origin}/project/${packet.id}`
    : `${window.location.origin}/p/${packet.id}`;
  const buildShareLink = useShareUrl();
  // Shown and copied.
  const shareLink = buildShareLink(packetUrl, { linkSource: LINK_SOURCE.COPY_LINK_PACKET_MODAL });
  // The social buttons get the referral code but not the copy-link utm params, which would
  // misreport every tweet and pin as a copied link. They carry no utm today, so this adds
  // attribution without rewriting what's already being measured.
  // Per provider, so a signup off a tweet is distinguishable from one off a pin.
  const referralCode = useReferralCode();
  const socialShareLink = (provider) =>
    withReferral(packetUrl, referralCode, LINK_SOURCE[`SOCIAL_${provider.toUpperCase()}`]);
  const [copied, setCopied] = useState(false);
  const { user } = useUser();

  const onClickCopy = () => {
    Mixpanel.track('Share Packet Link Copied', {
      type: 'button_copy',
      packet_id: packet.id,
      packet_title: packet.title,
    });
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 5000);
  };

  useEffect(() => {
    // hook into the copy event from browser
    const handleCopy = () => {
      Mixpanel.track('Share Packet Link Copied', {
        type: 'browser_copy',
        packet_id: packet.id,
        packet_title: packet.title,
      });
    };
    window.addEventListener('copy', handleCopy);
    return () => window.removeEventListener('copy', handleCopy);
  }, [packet]);

  return (
    <BasicModal
      showModal={true}
      setShowModal={onClose}
      title={`${packetTitleTruncated}`}
      icon={<ShareIcon className='w-5 h-5' />}>
      <div className='text-base text-gray-800 flex flex-col gap-y-4'>
        <div className='flex flex-col gap-y-1'>
          <p>Share via:</p>
          <div className='flex flex-row gap-x-2'>
            {shareProviders.map((provider) => (
              <WithTooltip
                key={provider.provider}
                text={provider.actionText}
                position='top'
                anchor='left'>
                <button
                  onClick={() => {
                    Mixpanel.track('Share Packet Clicked', {
                      packet_id: packet.id,
                      packet_title: packet.title,
                      provider: provider.provider,
                    });
                    provider.onClick(packet, user, socialShareLink(provider.provider));
                  }}
                  key={provider.provider}
                  className='h-10 w-10 hover:bg-gray-100 flex items-center justify-center p-1.5 border border-gray-200 rounded-full'>
                  {provider.icon}
                </button>
              </WithTooltip>
            ))}
          </div>
        </div>
        <div className='flex flex-col gap-y-1'>
          <p>Or copy link:</p>
          <div className='border border-gray-200 h-12 rounded-md p-2 truncate relative flex items-center gap-x-2'>
            <LinkIcon className='w-5 h-5 absolute left-2' />
            <p className='w-[78%] truncate absolute left-8'>{shareLink}</p>
            <button
              onClick={onClickCopy}
              className='hover:bg-emerald-500 px-2 py-1 absolute right-2 bg-emerald-600 flex items-center justify-center text-white rounded-md top-1/2 -translate-y-1/2'>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      </div>
    </BasicModal>
  );
};
